import { useState, useEffect } from "react";
import useSWR from "swr";
import { Form, Formik, FieldArray } from "formik";
import * as Yup from "yup";
import { fetcherSimple, fetcherFull } from "utils/api";
import {
    Button,
    IconButton,
    Tooltip,
    Typography,
} from "@material-tailwind/react";
import MaterialIcon from "components/material-icon";
import { emptyCustomAnswerData } from "utils/consts";
import CustomResultsDialog from "components/dialog/custom-results-dialog";
import FormikTextarea from "./form/formik-textarea";
import ConfirmReportDialog from "./dialog/confirm-report-dialog";

export default function CustomQuestionResponses(props) {
    const question = props.question;
    const responses = props.responses;
    const customResponses = props.responses.custom;

    const [submittingAnswer, setSubmittingAnswer] = useState(false);

    useEffect(() => {
        if (
            (!customResponses ||
                (customResponses && customResponses.length === 0)) &&
            responses.preset &&
            responses.preset.length === 0
        ) {
            props.setQuestionSaved(false);
        }
    }, [customResponses, responses.preset]);

    const makeEditable = (index, setFieldValue) => {
        // Update Formik's state to enable editing
        setFieldValue(`answer.${index}.saved`, false);
        props.update();
        props.setQuestionSaved(false);
    };

    const handleCustomSubmit = () => {
        // left as no-op since child handles submissions
    };

    const deleteCustomAnswer = async (
        values,
        answer,
        removeAnswer,
        addNewInput,
    ) => {
        if (props.previewModeState) return;

        if (
            answer.nanoid !== "" &&
            customResponses.find((c) => c.nanoid === answer.nanoid)
        ) {
            await props.setAnswers({
                preset: responses.preset,
                custom: responses.custom.filter(
                    (c) => c.nanoid !== answer.nanoid,
                ),
            });
            if (answer.is_creator) {
                await fetcherSimple(
                    `/v2/response/question/${question.nanoid}/answer/${answer.nanoid}`,
                    "DELETE",
                );
            }
            props.update();
        }
    };

    const createOrUpdateCustomAnswer = async (
        values,
        index,
        addNewInput,
        replaceResult,
        resetForm,
    ) => {
        if (props.previewModeState) return;
        setSubmittingAnswer(true);

        if (customResponses[index]?.text === values.answer[index]?.text) {
            customResponses[index].saved = true;
            props.update();
            resetForm({
                values: {
                    answer:
                        customResponses.length === 0
                            ? emptyCustomAnswerData
                            : question.answer_type === "single"
                                ? customResponses
                                : [...customResponses, ...emptyCustomAnswerData],
                },
            });
            if (values.answer[index]?.text) props.setQuestionSaved(true);
            setSubmittingAnswer(false);
            return;
        }

        let result;
        let isEdit = false;
        if (values.answer[index].nanoid !== "") {
            isEdit = true;
            result = await fetcherFull(
                `/v2/response/question/${question.nanoid}/answer/${values.answer[index].nanoid}`,
                "PUT",
                { text: values.answer[index].text },
            );
        } else {
            result = await fetcherFull(
                `/v2/response/question/${question.nanoid}/answer`,
                "POST",
                { text: values.answer[index].text },
            );
        }

        if (result.nanoid) {
            props.update(result);
            replaceResult(index, result);
            if (question.answer_type === "multiple" && !isEdit) {
                addNewInput(emptyCustomAnswerData[0]);
            }
            props.setQuestionSaved(true);
        }
        setSubmittingAnswer(false);
    };

    const CustomString = Yup.object().shape({
        custom: Yup.array().of(Yup.string().min(1, "Answer is Empty!")),
    });

    const validationSchemaCustom = Yup.object().shape({
        custom: Yup.array().of(CustomString),
    });

    return (
        <>
            {!!(question && customResponses) && (
                <Formik
                    initialValues={{
                        answer:
                            customResponses.length === 0
                                ? emptyCustomAnswerData
                                : question.answer_type === "single"
                                    ? customResponses
                                    : [
                                        ...customResponses,
                                        ...emptyCustomAnswerData,
                                    ],
                    }}
                    onSubmit={handleCustomSubmit}
                    validationSchema={validationSchemaCustom}
                    enableReinitialize
                >
                    {({ values, isValid, resetForm, setFieldValue }) => (
                        <Form className="font-body text-black sm:mt-4 mt-2">
                            {question.answer_type === "single"
                                ? "Your response"
                                : "Your response(s)"}
                            <FieldArray name="answer">
                                {({ push, remove, replace }) => (
                                    <>
                                        {values.answer.map(
                                            (singleAnswer, index) => (
                                                <CustomQuestionResponse
                                                    key={index}
                                                    index={index}
                                                    singleAnswer={singleAnswer}
                                                    values={values}
                                                    question={question}
                                                    makeEditable={(index) => makeEditable(index, setFieldValue)}
                                                    deleteCustomAnswer={
                                                        deleteCustomAnswer
                                                    }
                                                    createOrUpdateCustomAnswer={
                                                        createOrUpdateCustomAnswer
                                                    }
                                                    submittingAnswer={
                                                        submittingAnswer
                                                    }
                                                    isValid={isValid}
                                                    push={push}
                                                    remove={remove}
                                                    replace={replace}
                                                    resetForm={resetForm}
                                                    responses={responses}
                                                    update={props.update}
                                                    setAnswers={
                                                        props.setAnswers
                                                    }
                                                    previewModeState={
                                                        props.previewModeState
                                                    }
                                                    setQuestionSaved={
                                                        props.setQuestionSaved
                                                    }
                                                    setPresetOther={
                                                        props.setPresetOther
                                                    }
                                                />
                                            ),
                                        )}
                                    </>
                                )}
                            </FieldArray>
                        </Form>
                    )}
                </Formik>
            )}
        </>
    );
}

function CustomQuestionResponse({
    index,
    singleAnswer,
    values,
    question,
    makeEditable,
    deleteCustomAnswer,
    createOrUpdateCustomAnswer,
    submittingAnswer,
    isValid,
    push,
    remove,
    replace,
    resetForm,
    responses,
    update,
    setAnswers,
    previewModeState,
    setQuestionSaved,
    setPresetOther,
}) {
    const [searchTerm, setSearchTerm] = useState("");
    const [searchResults, setSearchResults] = useState({ found: 0 });
    const [openMatchPopover, setOpenMatchPopover] = useState(false);
    const [seenMatchPopover, setSeenMatchPopover] = useState(false);
    const [showCustomResultsDialog, setShowCustomResultsDialog] =
        useState(false);
    const [showConfirmReportDialog, setShowConfirmReportDialog] =
        useState(false);
    const [reportedAnswer, setReportedAnswer] = useState(null);

    const { data: customSearchResult, error: customSearchResultError } = useSWR(
        () =>
            !question.contains_pii &&
            searchTerm.length > 2
                ? [
                    `/v2/response/question/${question.nanoid}/answer?query=${searchTerm}`,
                    "GET",
                ]
                : null,
        ([path, method]) => fetcherSimple(path, method),
    );

    useEffect(() => {
        setSearchResults(customSearchResult);
        if (
            !question.contains_pii &&
            customSearchResult?.found > 0 &&
            seenMatchPopover === false
        ) {
            setOpenMatchPopover(true);
            setSeenMatchPopover(true);
        }
    }, [customSearchResult, seenMatchPopover, question.contains_pii]);

    const onEditInput = async (event) => {
        const value = event.target.value.trim();
        if (
            question.answer_type === "single" &&
            event.target.value.length > 0
        ) {
            setPresetOther(true);
        }
        if (singleAnswer) {
            if(value.length === 0) {
                setQuestionSaved(true);
            } else if (value.length > 0) {
                setQuestionSaved(false);
            }
        }
        setSearchTerm(value);
    };

    const addCustomAnswer = async (values) => {
        if (previewModeState) return;
        if (question.answer_type === "single") {
            if (!values.answer[0] || values.answer[0] === "") return null;
            let newAnswer = searchResults.hits
                .filter((p) => values.answer[0] === p.document.answer_nanoid)
                .map((a) => ({ nanoid: a.document.answer_nanoid }));
            setAnswers({ preset: [], custom: newAnswer });
        } else {
            const newAnswers = searchResults.hits
                .filter((p) =>
                    values.answer.some(
                        (ele) => ele === p.document.answer_nanoid,
                    ),
                )
                .map((a) => ({ nanoid: a.document.answer_nanoid }));
            newAnswers.push(...responses.custom);
            const onlyUnique = (value, index, self) =>
                self.findIndex((ele) => ele.nanoid === value.nanoid) === index;
            newAnswers.filter(onlyUnique);
            setAnswers({ preset: responses.preset, custom: newAnswers });
        }
        setSearchTerm("");
        update();
    };

    return (
        <div key={index}>
            {!!customSearchResultError && (
                <div className="font-body">Error getting search results!</div>
            )}

            <div className="flex flex-col font-body pb-2">
                {!!(
                    question.is_view_all &&
                    !question.contains_pii &&
                    !singleAnswer.saved
                ) && (
                    <div className="flex items-center mb-2 gap-2">
                        <div className="text-body font-bold text-black">
                            Matches:
                        </div>
                        <div>
                            <Tooltip
                                className="border rounded-xl border-blue-gray-500 bg-blue-500 shadow-xl shadow-black/10"
                                open={openMatchPopover}
                                handler={setOpenMatchPopover}
                                placement="right"
                                content={
                                    <div className="flex flex-row">
                                        <div>
                                            <Typography
                                                color="white"
                                                as="span"
                                                align="center"
                                            >
                                                You have matched responses.
                                                <br />
                                                Click on the number to view.
                                            </Typography>
                                        </div>
                                    </div>
                                }
                            >
                                <Button
                                    type="button"
                                    variant="filled"
                                    className="w-10 h-10 !p-0 flex items-center justify-center rounded-lg border-2 border-gray-600 text-sm text-gray-900"
                                    color={
                                        customSearchResult &&
                                        customSearchResult.found > 0
                                            ? "orange"
                                            : "white"
                                    }
                                    disabled={
                                        !customSearchResult ||
                                        customSearchResult.found === 0
                                    }
                                    onClick={() =>
                                        setShowCustomResultsDialog(true)
                                    }
                                >
                                    {customSearchResult
                                        ? customSearchResult.found
                                        : 0}
                                </Button>
                            </Tooltip>
                        </div>
                    </div>
                )}
                <div className="flex items-center">
                    <div className="grow md:mr-2">
                        <FormikTextarea
                            autoComplete="off"
                            placeholder="Add a response"
                            variant="static"
                            rows="1"
                            name={`answer.${index}.text`}
                            maxLength={question.answer_limit || 150}
                            className="min-h-0 focus:border-0 font-body pb-1"
                            disabled={singleAnswer.saved}
                            onKeyUp={onEditInput}
                        />
                    </div>
                    {!!(isValid && !singleAnswer.saved) && (
                        <IconButton
                            color={
                                isValid && !singleAnswer.saved
                                    ? "green"
                                    : "gray"
                            }
                            type="button"
                            variant="filled"
                            className="ml-1"
                            disabled={
                                submittingAnswer ||
                                !isValid ||
                                singleAnswer.saved ||
                                !values?.answer?.[index].text.trim().length
                            }
                            onClick={() =>
                                createOrUpdateCustomAnswer(
                                    values,
                                    index,
                                    push,
                                    replace,
                                    resetForm,
                                )
                            }
                        >
                            <MaterialIcon name="check" size="lg" />
                        </IconButton>
                    )}
                </div>
            </div>

            <div className="flex mt-0 border-b border-gray-400 mb-2 pb-1">
                <div className="grow text-platinumheavy md:font-display font-body text-xs md:text-sm">
                    {values?.answer?.[index].text.length} /{" "}
                    {question.answer_limit || 150} characters
                </div>
                <div>
                    {!!singleAnswer.is_creator && (
                        <IconButton
                            color={singleAnswer.saved ? "blue" : "gray"}
                            type="button"
                            variant="filled"
                            disabled={!isValid && !singleAnswer.saved}
                            onClick={() => makeEditable(index)}
                        >
                            <MaterialIcon name="edit" size="lg" />
                        </IconButton>
                    )}
                    {!!singleAnswer.saved && (
                        <IconButton
                            color="red"
                            type="button"
                            variant="filled"
                            className="ml-1"
                            disabled={!isValid}
                            onClick={() =>
                                deleteCustomAnswer(
                                    values,
                                    singleAnswer,
                                    remove,
                                    push,
                                )
                            }
                        >
                            <MaterialIcon name="close" size="lg" />
                        </IconButton>
                    )}
                </div>
            </div>

            <CustomResultsDialog
                showDialog={showCustomResultsDialog}
                setShowConfirmReportDialog={setShowConfirmReportDialog}
                setReportedAnswer={setReportedAnswer}
                searchResults={searchResults}
                searchTerm={searchTerm}
                question={question}
                responses={responses}
                saveSelection={addCustomAnswer}
                handleClose={() =>
                    setShowCustomResultsDialog(
                        (showCustomResultsDialog) => !showCustomResultsDialog,
                    )
                }
            />
            <ConfirmReportDialog
                showDialog={showConfirmReportDialog}
                showCustomResultsDialog={showCustomResultsDialog}
                question={question}
                reportedAnswer={reportedAnswer}
                setShowConfirmReportDialog={setShowConfirmReportDialog}
                setShowCustomResultsDialog={setShowCustomResultsDialog}
            />
        </div>
    );
}
